<template>
  <section class="section-grid">
    <CampaignsGridHeadline />
    <mq-layout class="grid-summary-m" :mq="['sm', 'md']">
      <div class="grid-summary-inner">
        <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ campaigns ?
          campaigns.Total : 0 }}</span>
      </div>
      <b-input :value="searchValue" :placeholder="$t('Campaigns.Grid.Search')" @blur="onSearchChange($event)"
        @keyup.native.enter="onSearchChange($event)" v-show="showSearchInput"></b-input>
      <b-button class="is-transparent grid-search-m" @click="showSearchInput = true">
        <icon-base width="17" height="17" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
      </b-button>
    </mq-layout>
    <CampaignsGridContent @adgroups-loaded="onAdGroupsLoaded" />
    <mq-layout class="grid-summary" mq="lg">
      <div class="grid-summary-inner">
        <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ campaigns ?
          campaigns.Total : 0 }}</span>
      </div>
    </mq-layout>
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { TableSettings, TableSettingsStateColumn } from "@/models/TableSettings";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignAdGroupGridViewModel } from "@/models/campaigns/CampaignAdGroupGridViewModel";
import CampaignsGridHeadline from "./CampaignsGridHeadline.vue";
import CampaignsGridContent from "./CampaignsGridContent.vue";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";
import IconCaretRight from "../icons/IconCaretRight.vue";
import IconSearch from "../icons/IconSearch.vue";


Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsGridHeadline,
    CampaignsGridContent,
    IconBase,
    IconSearch,
    IconCaretDown,
    IconCaretRight,
    IconCaretLeft,
  },
})
export default class CampaignsGrid extends Vue {

  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedCampaignGridColumnsIds", { namespace }) selectedCampaignGridColumnsIds!: number[];
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Action("setColumns", { namespace }) setColumns!: (columns: GridColumn[]) => void;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  campaignsGridColumns: GridColumn[] = [
    { id: 0, name: "ChannelId", label: "ID", visible: false, forbidden: false, searchable: true, sortable: true, width: 80 },
    {
      id: 1,
      name: "Actions",
      label: i18n.t("Campaigns.Grid.Columns.Actions").toString(),
      searchable: false,
      sortable: true,
      visible: true,
      forbidden: false,
    },
    { id: 2, name: "ChannelTitle", label: i18n.t("Campaigns.Grid.Columns.ChannelTitle").toString(), class: "cell-title", searchable: true, sortable: true, visible: true, forbidden: false, width: 300 },
    { id: 3, name: "ChannelTypeString", label: i18n.t("Campaigns.Grid.Columns.ChannelTypeString").toString(), class: "cell-channel-metadata-type-string", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 4, name: "CompanyTitle", label: i18n.t("Campaigns.Grid.Columns.CompanyTitle").toString(), class: "cell-company-title", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 5, name: "DefaultAssetTitle", label: i18n.t("Campaigns.Grid.Columns.DefaultAssetTitle").toString(), class: "cell-default-asset-title", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 6, name: "DefaultAssetUrl", label: i18n.t("Campaigns.Grid.Columns.DefaultAssetUrl").toString(), class: "cell-default-asset-url", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 7, name: "StatusStr", label: i18n.t("Campaigns.Grid.Columns.StatusStr").toString(), class: "cell-status-str", searchable: false, sortable: false, visible: true, forbidden: true, width: 400 },
    { id: 8, name: "BudgetString", label: i18n.t("Campaigns.Grid.Columns.BudgetString").toString(), class: "cell-budget-string", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 31, name: "BudgetMedia", label: i18n.t("Campaigns.Grid.Columns.BudgetMedia").toString(), class: "cell-budget-media", searchable: false, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 9, name: "GoalString", label: i18n.t("Campaigns.Grid.Columns.GoalString").toString(), class: "cell-goal-string", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 10, name: "AverageLeadCost", label: i18n.t("Campaigns.Grid.Columns.AverageLeadCost").toString(), class: "cell-average-lead-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 11, name: "Cost", label: i18n.t("Campaigns.Grid.Columns.Cost").toString(), class: "cell-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 12, name: "Conversion", label: i18n.t("Campaigns.Grid.Columns.Conversion").toString(), class: "cell-conversion", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 13, name: "CTR", label: i18n.t("Campaigns.Grid.Columns.CTR").toString(), class: "cell-ctr", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 14, name: "LeadCount", label: i18n.t("Campaigns.Grid.Columns.LeadCount").toString(), class: "cell-lead-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 15, name: "ClickCount", label: i18n.t("Campaigns.Grid.Columns.ClickCount").toString(), class: "cell-click-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 16, name: "ViewCount", label: i18n.t("Campaigns.Grid.Columns.ViewCount").toString(), class: "cell-view-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 17, name: "VisitorCount", label: i18n.t("Campaigns.Grid.Columns.VisitorCount").toString(), class: "cell-visitor-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 18, name: "Phone", label: i18n.t("Campaigns.Grid.Columns.Phone").toString(), class: "cell-phone", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 20, name: "RedirectPhone", label: i18n.t("Campaigns.Grid.Columns.RedirectPhone").toString(), class: "cell-redirect-phone", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 21, name: "ImpressionCount", label: i18n.t("Campaigns.Grid.Columns.ImpressionCount").toString(), class: "cell-impression-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 22, name: "MeetingCount", label: i18n.t("Campaigns.Grid.Columns.MeetingCount").toString(), class: "cell-meeting-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 23, name: "DealCount", label: i18n.t("Campaigns.Grid.Columns.DealCount").toString(), class: "cell-deal-count", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 24, name: "DealAmount", label: i18n.t("Campaigns.Grid.Columns.DealAmount").toString(), class: "cell-deal-amount", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 25, name: "AverageClickCost", label: i18n.t("Campaigns.Grid.Columns.AverageClickCost").toString(), class: "cell-average-click-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 26, name: "AverageViewCost", label: i18n.t("Campaigns.Grid.Columns.AverageViewCost").toString(), class: "cell-average-view-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 27, name: "AverageVisitorCost", label: i18n.t("Campaigns.Grid.Columns.AverageVisitorCost").toString(), class: "cell-average-visitor-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 28, name: "AverageImpressionCost", label: i18n.t("Campaigns.Grid.Columns.AverageImpressionCost").toString(), class: "cell-average-impression-cost", searchable: false, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 29, name: "GoalStartDate", label: i18n.t("Campaigns.Grid.Columns.GoalStartDate").toString(), class: "cell-start-date", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    { id: 30, name: "GoalEndDate", label: i18n.t("Campaigns.Grid.Columns.GoalEndDate").toString(), class: "cell-end-date", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
  ];

  showSearchInput = false;

  //#endregion Properties


  //#region Watchers

  @Watch("tableSettings", { immediate: true })
  onTableSettingsChange(newValue: TableSettings[], oldValue: TableSettings[]): void {
    if (newValue === undefined) {
      return;
    } else {
      let settings: TableSettings | undefined = newValue.find((i: TableSettings) => i.GridId === "channelsgrid");
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  @Watch("campaignsPageData", { immediate: true })
  onCampaignsPageDataChange(newValue: CampaignsPageViewModel, oldValue: CampaignsPageViewModel): void {
    if (oldValue === undefined || newValue === null) {
      return;
    } else {
      let settings: TableSettings | undefined = this.tableSettings.find((i: TableSettings) => i.GridId === "channelsgrid");
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignsGridColumnsFiltered(): GridColumn[] {
    // if no selected columns (like on first page load), select all columns
    if (this.selectedCampaignGridColumnsIds.length === 0) {
      for (let i = 0; i < this.campaignsGridColumns.length; i++) {
        if (this.campaignsGridColumns[i].visible && !this.campaignsGridColumns[i].forbidden) {
          this.selectedCampaignGridColumnsIds.push(this.campaignsGridColumns[i].id);
        }
      }
    }
    for (let i = 0; i < this.campaignsGridColumns.length; i++) {
      this.campaignsGridColumns[i].visible = this.selectedCampaignGridColumnsIds.indexOf(this.campaignsGridColumns[i].id) >= 0;
    }
    return this.campaignsGridColumns;
  }

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    this.setColumns(this.campaignsGridColumnsFiltered);
  }

  //#endregion Lifecycle


  //#region Handlers

  onAdGroupsLoaded(adGroups: CampaignAdGroupGridViewModel[]) {
    this.$emit("adgroups-loaded", adGroups);
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  //#endregion Handlers


  //#region Private methods

  private updateColumns(tableSettings: TableSettings) {
    const newColumns: GridColumn[] = JSON.parse(JSON.stringify(this.columns));
    for (let i = 0; i < newColumns.length; i++) {
      const tableSettingsColumn: TableSettingsStateColumn | undefined = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
        let columnName: string | undefined = column.field;
        if (!columnName) {
          columnName = column.title;
        }
        switch (columnName) {
          case "Actions":
          case "פעולות":
            columnName = "Actions"
            break;
        }
        return columnName === newColumns[i].name;
      });

      newColumns[i].visible = !tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden;
    }
    this.setColumns(newColumns);
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-grid {
  background: #fff;
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  margin-bottom: 30px;
}

.grid-bottom {
  padding: 9px 15px 11px;

  .grid-bottom-inner {
    display: flex;
    justify-content: space-between;
  }

  .per-page {
    .per-page-label {
      font-weight: 600;
    }

    .per-page-dropdown {
      .button-text-w {
        margin-left: 7px;
      }

      a.dropdown-item,
      .dropdown .dropdown-menu .has-link a,
      .dropdown .dropdown-menu .has-link a,
      button.dropdown-item {
        padding: 3px 5px;
      }
    }
  }

  .grid-show {
    display: flex;

    .show-text {
      margin-left: 5px;
    }

    .show-nav {
      display: flex;
      align-items: center;

      .show-nav-button {
        padding: 0;
        width: 30px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &:hover {
          color: $link;
        }

        &.show-nav-button-prev {}

        &.show-nav-button-next {}
      }
    }
  }
}

.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;

  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}

.grid-summary-m {
  height: 49px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #435c65;

  .button.grid-search-m {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 49px;
  }

  .grid-summary-inner {
    padding: 10px 15px;

    .summary-label {
      font-weight: 600;
    }
  }

  .control.is-clearfix {
    min-width: 230px;
  }
}

html[dir="ltr"] {
  .grid-bottom {
    .per-page {
      .per-page-dropdown {
        .button-text-w {
          margin: 0 7px 0 0;
        }
      }
    }

    .grid-show {
      .show-text {
        margin: 0 5px 0 0;
      }

      .show-nav {
        .show-nav-button {
          transform: scaleX(-1);
        }
      }
    }
  }
}</style>
